import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import { mergeDataGridStyles } from '../../styles/dataGridStyles';
import tbtSubmissionService from '../../services/tbtSubmissionService';
import TopicSelectionModal from './TopicSelectionModal';

const TBTList = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [topicModalOpen, setTopicModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadSubmissions();
    }, []);

    const loadSubmissions = async () => {
        setLoading(true);
        try {
            const list = await tbtSubmissionService.listIncomplete();

            // Sort by updatedAt descending
            list.sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt || 0);
                const dateB = new Date(b.updatedAt || b.createdAt || 0);
                return dateB.getTime() - dateA.getTime();
            });

            setSubmissions(list);
        } catch (error) {
            console.error('Error loading TBT submissions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setTopicModalOpen(true);
    };

    const handleTopicSelected = async (topic) => {
        try {
            const newSubmission = await tbtSubmissionService.create(topic.id, topic.title);
            setTopicModalOpen(false);
            navigate(`/tbt/${newSubmission.id}/edit`);
        } catch (error) {
            console.error('Error creating TBT submission:', error);
        }
    };

    const handleEdit = (id) => {
        navigate(`/tbt/${id}/edit`);
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedId !== null) {
            try {
                await tbtSubmissionService.delete(selectedId);
                await loadSubmissions();
            } catch (error) {
                console.error('Error deleting TBT submission:', error);
            }
        }
        setDeleteDialogOpen(false);
        setSelectedId(null);
    };

    const columns = [
        {
            field: 'topicTitle',
            headerName: 'Topic',
            flex: 2,
            minWidth: 250
        },
        {
            field: 'signedCount',
            headerName: 'Signatures',
            flex: 1,
            minWidth: 120,
            valueGetter: (_, row) => {
                const employees = row.employees || [];
                const signedCount = employees.filter(emp => emp.signature).length;
                const totalCount = employees.length;
                return `${signedCount} / ${totalCount}`;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(params.row.id);
                        }}
                        color="primary"
                        title="Edit TBT"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(params.row.id);
                        }}
                        color="error"
                        title="Delete TBT"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        }
    ];

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    flex: '0 0 auto'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Tool Box Talks</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Incomplete submissions
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        New TBT
                    </Button>
                </Box>
            </Box>

            {/* DataGrid Container */}
            <Box
                sx={{
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    p: 2
                }}
            >
                <DataGrid
                    rows={submissions}
                    columns={columns}
                    loading={loading}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25 }
                        }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    onRowClick={(params) => handleEdit(params.row.id)}
                    rowHeight={52}
                    sx={mergeDataGridStyles({
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'pointer',
                            backgroundColor: 'action.hover',
                        },
                    })}
                />
            </Box>

            {/* Topic Selection Modal */}
            <TopicSelectionModal
                open={topicModalOpen}
                onClose={() => setTopicModalOpen(false)}
                onSelect={handleTopicSelected}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete TBT?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this Tool Box Talk submission? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TBTList;
